import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import StateBlock from '../ui/StateBlock';
import { getInitials, getAvatarColor } from '../../hooks/useLiveChatNotifications';
import { ChevronLeft } from 'lucide-react';
import { buildDiscussionPayload } from '../../utils/newDiscussionModel';
import { directChatTitle, isCustomerRole, matchesChatSearch, personRows, type ChatGroupLike } from '../../utils/chatScreenModel';

interface Person { id: string; name: string; role: string; organization_id?: string | null; email?: string | null }

/**
 * D-326 - open the 1:1 chat with a person AT ONCE: the existing one (no duplicates; an archived one
 * is reopened), else a new one with no form, no subject and no picking.
 */
export function useOpenDirectChat(onOpened: (ticketId: string) => void) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const me = useAuthStore((s) => s.user);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState('');

  const open = async (p: Person, known: { ticketId: string | null } = { ticketId: null }) => {
    if (!me || busyId) return;
    setBusyId(p.id);
    setError('');
    try {
      let id = known.ticketId;
      if (!id) {
        const found = (await api.getDirectConversation(p.id)).data;
        if (found) {
          id = found.id;
          if (found.status === 'completed') await api.reopenTicket(found.id).catch(() => undefined);
        }
      }
      if (!id) {
        const payload = buildDiscussionPayload({
          openerId: me.id,
          title: directChatTitle(p.name),
          message: '',
          organizationId: isCustomerRole(p.role) ? (p.organization_id ?? '') : '',
          picked: [p.id],
          people: [{ id: p.id, role: p.role, name: p.name, email: p.email ?? '' }],
          kind: 'conversation',
        });
        id = (await api.createTicket(payload)).data?.id ?? null;
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: ['conversations'] }),
          queryClient.invalidateQueries({ queryKey: ['conversations-grouped'] }),
        ]);
      }
      if (id) onOpened(id); else setError(t('chatScreen.openFailed'));
    } catch {
      setError(t('chatScreen.openFailed'));
    } finally {
      setBusyId(null);
    }
  };
  return { open, busyId, error };
}

function PersonButton({ name, sub, online, unread, busy, onClick }: {
  name: string; sub?: string | null; online?: boolean; unread?: number; busy?: boolean; onClick: () => void;
}) {
  const { t } = useTranslation();
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={busy}
      aria-busy={busy || undefined}
      data-testid="chat-person"
      className="w-full flex items-center gap-3 px-3 py-3 text-start transition-colors border-b border-surface-200/80 hover:bg-surface-50 disabled:opacity-60"
    >
      <div className="relative flex-shrink-0">
        <div className={`w-12 h-12 rounded-full ${getAvatarColor(name)} text-white font-semibold flex items-center justify-center text-sm`}>{getInitials(name)}</div>
        {online && (
          <span
            className="absolute bottom-0 -end-0.5 w-3 h-3 rounded-full bg-solid-success ring-2 ring-surface-0"
            data-testid="presence-dot"
            title={t('chatScreen.online')}
            aria-label={t('chatScreen.online')}
          />
        )}
      </div>
      <div className="flex-1 min-w-0">
        <span className="block text-sm font-medium text-surface-800 truncate">{name}</span>
        {sub && <span className="block text-xs text-surface-500 truncate">{sub}</span>}
      </div>
      {!!unread && unread > 0 && (
        <span className="min-w-5 h-5 px-1 bg-solid-success text-white text-xs font-semibold tabular-nums rounded-full flex items-center justify-center">{unread > 9 ? '9+' : unread}</span>
      )}
    </button>
  );
}

/** "עובדים": every active OTE employee; a green dot = online now; a click = the chat. */
export function EmployeesList({ groups, onlineIds, search, onOpen, busyId }: {
  groups: readonly ChatGroupLike[];
  onlineIds: ReadonlySet<string>;
  search: string;
  onOpen: (p: Person, known: { ticketId: string | null }) => void;
  busyId: string | null;
}) {
  const { t } = useTranslation();
  const me = useAuthStore((s) => s.user);
  const { data, isLoading, error, refetch, isFetching } = useQuery({
    queryKey: ['users', 'ote-staff-for-chat-screen'],
    queryFn: () => api.getUsers({ ote_staff: true, active: true, limit: 500 }),
    staleTime: 30_000,
  });
  if (isLoading) return <StateBlock kind="loading" rows={6} />;
  if (error) return <StateBlock kind="error" title={t('states.discussions.listError')} error={error} onRetry={() => void refetch()} retrying={isFetching} />;
  const people = ((data?.data ?? []) as Person[]).filter((u) => matchesChatSearch([u.name, u.email], search));
  const rows = personRows(people, groups, me?.id, onlineIds);
  if (rows.length === 0) return <StateBlock kind="empty" title={t('chatScreen.noEmployees')} description={null} />;
  return (
    <>
      {rows.map((r) => (
        <PersonButton key={r.user.id} name={r.user.name} online={r.online} unread={r.unread} busy={busyId === r.user.id} onClick={() => onOpen(r.user, { ticketId: r.ticketId })} />
      ))}
    </>
  );
}

/** "לקוחות": the customers; a click = that customer's people (logins), a click on a person = the chat. */
export function CustomersList({ groups, search, onOpen, busyId }: {
  groups: readonly ChatGroupLike[];
  search: string;
  onOpen: (p: Person, known: { ticketId: string | null }) => void;
  busyId: string | null;
}) {
  const { t } = useTranslation();
  const me = useAuthStore((s) => s.user);
  const [org, setOrg] = useState<{ id: string; name: string } | null>(null);
  const orgsQ = useQuery({
    queryKey: ['organizations', 'for-chat-screen'],
    queryFn: () => api.getOrganizations({ status: 'active', limit: 500 }),
    staleTime: 60_000,
  });
  const peopleQ = useQuery({
    queryKey: ['users', 'customer-people-for-chat-screen', org?.id],
    queryFn: () => api.getUsers({ organization_id: org!.id, active: true, limit: 500 }),
    enabled: !!org,
    staleTime: 30_000,
  });

  if (org) {
    const people = ((peopleQ.data?.data ?? []) as Person[]).filter((u) => isCustomerRole(u.role) && matchesChatSearch([u.name, u.email], search));
    const rows = personRows(people, groups, me?.id, new Set());
    return (
      <>
        <button type="button" onClick={() => setOrg(null)} className="w-full flex items-center gap-2 px-3 py-2 text-sm text-accent-text border-b border-surface-200/80 hover:bg-surface-50">
          <ChevronLeft size={16} className="rtl:rotate-180" aria-hidden="true" />
          <span className="truncate">{t('chatScreen.backToCustomers')} · {org.name}</span>
        </button>
        {peopleQ.isLoading && <StateBlock kind="loading" rows={4} />}
        {peopleQ.error && <StateBlock kind="error" title={t('states.discussions.listError')} error={peopleQ.error} onRetry={() => void peopleQ.refetch()} retrying={peopleQ.isFetching} />}
        {!peopleQ.isLoading && !peopleQ.error && rows.length === 0 && <StateBlock kind="empty" title={t('chatScreen.noPeople')} description={null} />}
        {rows.map((r) => (
          <PersonButton key={r.user.id} name={r.user.name} sub={r.user.email} unread={r.unread} busy={busyId === r.user.id} onClick={() => onOpen({ ...r.user, organization_id: r.user.organization_id ?? org.id }, { ticketId: r.ticketId })} />
        ))}
      </>
    );
  }

  if (orgsQ.isLoading) return <StateBlock kind="loading" rows={6} />;
  if (orgsQ.error) return <StateBlock kind="error" title={t('states.discussions.listError')} error={orgsQ.error} onRetry={() => void orgsQ.refetch()} retrying={orgsQ.isFetching} />;
  const orgs = ((orgsQ.data?.data ?? []) as Array<{ id: string; name: string; is_internal?: boolean }>)
    .filter((o) => !o.is_internal && matchesChatSearch([o.name], search));
  if (orgs.length === 0) return <StateBlock kind="empty" title={t('chatScreen.noCustomers')} description={null} />;
  return (
    <>
      {orgs.map((o) => (
        <PersonButton key={o.id} name={o.name} onClick={() => setOrg({ id: o.id, name: o.name })} />
      ))}
    </>
  );
}
